import React, { useState, useEffect, useCallback } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Input } from '../ui/Input'
import { Button } from '../ui/Button'
import { IngredientsService } from '../../services/api/ingredientsService'
import { Ingredient } from '../../types/ingredient'
import { useToast } from '../../hooks/useToast'
import { useDebounce } from '../../hooks/useDebounce'

interface MergeIngredientDialogProps {
  isOpen: boolean
  onClose: () => void
  currentIngredient: Ingredient
  onMerged?: (newIngredientId: string) => void
}

export const MergeIngredientDialog: React.FC<MergeIngredientDialogProps> = ({
  isOpen,
  onClose,
  currentIngredient,
  onMerged
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<Ingredient[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isMerging, setIsMerging] = useState(false)
  const { showToast } = useToast()
  const debouncedSearchQuery = useDebounce(searchQuery, 500)

  const performSearch = useCallback(async (query: string) => {
    setIsSearching(true)
    try {
      const results = await IngredientsService.searchIngredients(query, {
        limit: 50,
        page: 1,
        toValidate: false
      })
      // Filter out the current ingredient
      setSearchResults(results.filter(i => i.uuid !== currentIngredient.uuid))
    } catch (error) {
      console.error('Search error:', error)
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }, [currentIngredient.uuid])

  useEffect(() => {
    if (debouncedSearchQuery.length >= 2) {
      performSearch(debouncedSearchQuery)
    } else {
      setSearchResults([])
    }
  }, [debouncedSearchQuery, performSearch])

  const handleMerge = async (targetIngredient: Ingredient) => {
    setIsMerging(true)
    try {
      await IngredientsService.mergeIngredient(currentIngredient.uuid, targetIngredient.uuid)
      showToast(`Ingrédient fusionné avec succès avec ${targetIngredient.inciName}`, 'success')
      // Pass the target ingredient UUID since we merged INTO it
      onMerged?.(targetIngredient.uuid)
      onClose()
    } catch (error) {
      console.error('Merge error:', error)
      showToast('Erreur lors de la fusion des ingrédients', 'error')
    } finally {
      setIsMerging(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role="button"
        tabIndex={0}
        aria-label="Close dialog"
      />
      
      {/* Dialog */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-medium text-gray-900">
            Merger avec un autre ingrédient
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-2">
                Ingrédient actuel : <span className="font-normal">{currentIngredient.inciName}</span>
              </p>
            </div>

            <div>
              <label htmlFor="search-ingredient" className="block text-sm font-normal text-gray-700 mb-2">
                Rechercher un ingrédient par nom INCI
              </label>
              <Input
                id="search-ingredient"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Entrez le nom INCI de l'ingrédient..."
                className="w-full"
              />
            </div>

            <div>
              <p className="text-sm text-gray-600 mb-2">
                {searchResults.length} résultat(s) trouvé(s)
              </p>
            </div>

            {/* Search Results */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {isSearching ? (
                <div className="py-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-2 text-gray-600">Recherche en cours...</p>
                </div>
              ) : searchResults.length === 0 && searchQuery.length >= 2 ? (
                <div className="py-8 text-center text-gray-500">
                  Aucun résultat trouvé
                </div>
              ) : (
                searchResults.map((ingredient) => (
                  <div
                    key={ingredient.uuid}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                  >
                    <div>
                      <div className="font-normal text-gray-900">
                        {ingredient.inciName}
                      </div>
                      {ingredient.commonName && (
                        <div className="text-sm text-gray-600">
                          {ingredient.commonName}
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={() => handleMerge(ingredient)}
                      disabled={isMerging}
                      size="sm"
                    >
                      {isMerging ? 'Fusion...' : 'Merger'}
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}